import dayjs from '@/utils/dayjs';
import { isValidMMDDYYYY } from './validators.js';

/**
 * Formats a date. (Implementation currently empty)
 */
export const getFormattedDate = () => {};

/**
 * Checks time difference. (Implementation currently empty)
 */
export const checkTimeDiff = () => {};

/**
 * Formats seconds into MM:SS format.
 * @param {number} seconds - The number of seconds.
 * @returns {string} The formatted string "MM:SS".
 */
export const formatSeconds = (seconds) => {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  const formattedMinutes = min > 9 ? `${min}` : `0${min}`;
  const formattedSeconds = sec > 9 ? `${sec}` : `0${sec}`;
  return `${formattedMinutes}:${formattedSeconds}`;
};

/**
 * Converts seconds to a timer format (M:S).
 * @param {number} seconds - The number of seconds.
 * @returns {string} The formatted time string.
 */
export const convertToTimerFormat = (seconds) => {
  return `${
    Math.floor(seconds / 60) > 9
      ? Math.floor(seconds / 60)
      : '0' + Math.floor(seconds / 60)
  }:${
    Math.floor(seconds % 60) > 9
      ? Math.floor(seconds % 60)
      : '0' + Math.floor(seconds % 60)
  }`;
};

/**
 * Formats a date string from DD/MM/YYYY to "DD Mon, Weekday".
 * @param {string} inputDate - The date string in DD/MM/YYYY format.
 * @returns {string} The formatted date string (e.g., "11 May, Mon").
 */
export const formatDate = (inputDate) => {
  const [day, month, year] = inputDate.split('/').map(Number);

  const utcDate = new Date(year, month - 1, day); // month is 0-indexed

  const options = { day: '2-digit', month: 'short', weekday: 'short' };
  const formatted = new Intl.DateTimeFormat('en-GB', options);

  // Output will be something like: "Mon, 11 May"
  // We want: "11 May, Mon"
  const [weekday, d, mon] = formatted.format(utcDate).split(' ');

  return `${d} ${mon}, ${weekday?.split(',')?.[0]}`;
};

/**
 * Gets the epoch timestamp in milliseconds for midnight of the given date.
 * @param {number|string|Date} epochTimestamp - The date input.
 * @returns {number} The epoch timestamp in milliseconds.
 */
export const getMidnightEpoch = (epochTimestamp) => {
  const date = new Date(epochTimestamp);
  date.setHours(0, 0, 0, 0); // set to midnight local time
  return date.getUTCSeconds() * 1000; // return epoch timestamp in ms
};

/**
 * Gets the UTC midnight epoch timestamp for a given date.
 * @param {Date|string|number} inputDate - The input date.
 * @returns {number} The UTC midnight epoch timestamp.
 * @throws {Error} If the date format is invalid.
 */
export const getMidnightEpochUTC = (inputDate) => {
  let date;
  if (inputDate instanceof Date) {
    date = new Date(inputDate);
  } else if (typeof inputDate === 'string' || typeof inputDate === 'number') {
    date = new Date(inputDate);
  } else {
    throw new Error('Invalid date format.');
  }
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date input.');
  }
  // Get UTC midnight
  const utcMidnight = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  );
  return utcMidnight;
};

/**
 * Returns the epoch timestamp in milliseconds for midnight of the given date in the specified timezone.
 *
 * @param {string} dateStr - A date string (e.g., "2025-05-25").
 * @param {string} timeZone - IANA time zone string (e.g., "Asia/Kolkata", "America/New_York").
 * @returns {number} Epoch timestamp (in milliseconds) for midnight in the given time zone.
 */
export const getMidnightEpochInTimezone = (dateStr, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(dateStr));

  // Extract year, month, day
  const dateParts = {};
  for (const part of parts) {
    if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
      dateParts[part.type] = parseInt(part.value, 10);
    }
  }

  // Create a date at midnight in the given time zone
  const utcDate = new Date(
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 0, 0, 0, 0),
  );

  // Offset the UTC date to the target time zone using the same formatter
  // const formatter = new Intl.DateTimeFormat('en-US', {
  //   timeZone,
  //   hour12: false,
  //   hour: '2-digit',
  //   minute: '2-digit',
  //   second: '2-digit',
  // });

  const midnightInTz = new Date(utcDate.toLocaleString('en-US', { timeZone }));

  return midnightInTz.getTime();
};

/**
 * Gets the timestamp for the previous Sunday at midnight.
 * @returns {number} The timestamp in milliseconds.
 */
export const getPreviousSundayMidnightTimestamp = () => {
  const now = new Date();
  const day = now.getDay(); // 0 (Sun) to 6 (Sat)

  // Calculate how many days to subtract to reach previous Sunday
  const diff = day === 0 ? 7 : day;

  const sunday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - diff,
    0,
    0,
    0,
    0, // Midnight time
  );

  return sunday.getTime(); // Milliseconds since epoch
};

/**
 * Gets the timestamp for the next Saturday at midnight.
 * @returns {number} The timestamp in milliseconds.
 */
export const getNextSaturdayMidnightTimestamp = () => {
  const now = new Date();
  const day = now.getDay(); // 0 (Sun) to 6 (Sat)

  // Days to add to reach next Saturday
  const daysToAdd = (6 - day + 1) % 7 || 7;

  const nextSaturday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + daysToAdd,
    0,
    0,
    0,
    0, // Midnight
  );

  return nextSaturday.getTime(); // Milliseconds since epoch
};

/**
 * Converts DD/MM/YYYY date string to epoch milliseconds.
 * @param {string} dateString - The date string in DD/MM/YYYY format.
 * @returns {number} The epoch timestamp in milliseconds.
 */
export const convertDdMmYyyyToEpoch = (dateString) => {
  // Split the date string into day, month, and year components
  const parts = dateString.split('/');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date constructor
  const year = parseInt(parts[2], 10);

  // Create a Date object (using UTC to avoid timezone issues)
  const dateObject = new Date(year, month, day);

  // Get the epoch time in milliseconds
  const epochMilliseconds = dateObject.getTime();

  // Convert to epoch time in seconds
  // const epochSeconds = Math.floor(epochMilliseconds / 1000);

  return epochMilliseconds;
};

/**
 * Converts DD/MM/YYYY date string to UTC epoch milliseconds.
 * @param {string} dateString - The date string in DD/MM/YYYY format.
 * @returns {number} The epoch timestamp in milliseconds.
 */
export const convertDdMmYyyyToUTCEpoch = (dateString) => {
  // Split the date string into day, month, and year components
  const parts = dateString.split('/');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date constructor
  const year = parseInt(parts[2], 10);

  // Create a Date object (using UTC to avoid timezone issues)
  const dateObject = new Date(Date.UTC(year, month, day));

  // Get the epoch time in milliseconds
  const epochMilliseconds = dateObject.getTime();

  // Convert to epoch time in seconds
  // const epochSeconds = Math.floor(epochMilliseconds / 1000);

  return epochMilliseconds;
};

/**
 * Converts MM-DD-YYYY date string to epoch milliseconds.
 * @param {string} dateString - The date string in MM-DD-YYYY format.
 * @returns {Object} An object containing the timestamp or error status.
 */
export const convertMmDdYyyyToEpoch = (dateString) => {
  if (isValidMMDDYYYY(dateString)) {
    // Split the date string into day, month, and year components
    const parts = dateString.split('-');
    const day = parseInt(parts[1], 10);
    const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed in Date constructor
    const year = parseInt(parts[2], 10);

    // Create a Date object (using UTC to avoid timezone issues)
    const dateObject = new Date(year, month, day);

    // Get the epoch time in milliseconds
    const epochMilliseconds = dateObject.getTime();

    // Convert to epoch time in seconds
    // const epochSeconds = Math.floor(epochMilliseconds / 1000);

    return { timestamp: epochMilliseconds, error: false };
  } else {
    return {
      timestamp: null,
      error: true,
    };
  }
};

/**
 * Finds the difference in days between two dates.
 * @param {Date|string} date1 - The first date.
 * @param {Date|string} date2 - The second date.
 * @returns {number} The difference in days.
 */
export const findDayDifference = (date1, date2) => {
  const diffDays = dayjs(date1)
    .startOf('day')
    .diff(dayjs(date2).startOf('day'), 'days');

  return diffDays;
};

/**
 * Converts a difference in days to a "time ago" string format (d, w, m).
 * @param {number} difference - The difference in days.
 * @returns {string} The formatted string (e.g., "5d", "2w", "1m").
 */
export const timeAgo = (difference) => {
  return difference <= 28
    ? `${difference}d`
    : difference > 28 && difference <= 91
    ? `${Math.floor(difference / 7)}w`
    : `${Math.floor(difference / 28)}m`;
};

/**
 * Detects the user's timezone.
 * @returns {Object} An object containing the timezone and error status.
 */
export const detectTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return { timezone, errorInTimezone: false };
  } catch (error) {
    console.error('Error detecting timezone:', error);
    return { timezone: '', errorInTimezone: true };
  }
};

/**
 * Formats a timestamp to ISO string with timezone offset.
 * @param {number|string|Date} timestamp - The input timestamp.
 * @param {string} timeZone - The target timezone.
 * @returns {string} The ISO string with offset.
 */
export const toISOWithOffset = (timestamp, timeZone) => {
  const date = new Date(timestamp);

  // Use Intl to get date/time parts in the desired timezone
  const options = {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(date);

  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const d = parts.find((p) => p.type === 'day').value;
  const h = parts.find((p) => p.type === 'hour').value;
  const min = parts.find((p) => p.type === 'minute').value;
  const s = parts.find((p) => p.type === 'second').value;

  // Calculate the offset for that time zone
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  const offsetMinutes = (tzDate - utcDate) / 60000;

  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const offsetMins = String(absOffset % 60).padStart(2, '0');

  return `${y}-${m}-${d}T${h}:${min}:${s}${sign}${offsetHours}:${offsetMins}`;
};
